import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useContent, useSectionMeta } from "../content/ContentContext";
import { Reveal, Chapter, EASE_OUT } from "./Reveal";
import { WatchModal } from "./WatchModal";

const EpisodeCard = ({ ep, index, isLatest, onWatch }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: (index % 3) * 0.1, ease: EASE_OUT }}
    className={`group relative bg-coal rounded-sm overflow-hidden border transition-[border-color,box-shadow] duration-500 flex flex-col ${
      isLatest
        ? "border-gold/60 shadow-[0_0_60px_rgba(184,134,11,0.12)]"
        : "border-white/10 hover:border-gold/40"
    }`}
    data-testid={`episode-card-${ep.num}`}
  >
    <div className="relative aspect-video overflow-hidden">
      <img
        src={ep.img}
        alt={ep.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover img-cinematic transition-transform duration-[1400ms] ease-out group-hover:scale-[1.07]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-coal via-transparent to-transparent" />
      <span className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-ivory/80 bg-ink/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
        EP {String(ep.num).padStart(2, "0")}
      </span>
      {isLatest && (
        <span
          className="absolute top-4 right-4 text-[10px] tracking-[0.3em] uppercase text-ink bg-gold px-3 py-1.5 rounded-full font-semibold"
          data-testid="episode-latest-badge"
        >
          Latest
        </span>
      )}
    </div>
    <div className="p-6 md:p-7 flex flex-col flex-1">
      <h3 className="font-display text-xl md:text-2xl leading-snug text-ivory group-hover:text-gold-light transition-colors duration-500">
        {ep.title}
      </h3>
      <p className="text-sm text-smoke mt-3 leading-relaxed flex-1">{ep.desc}</p>
      <button
        onClick={() => onWatch(ep)}
        className="mt-6 self-start inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-gold-light hover:text-ivory transition-colors duration-300"
        data-testid={`episode-watch-button-${ep.num}`}
      >
        <Play size={11} className="fill-current" />
        {ep.teaser && !ep.videoId ? "Watch teaser" : "Watch episode"}
      </button>
    </div>
  </motion.article>
);

export const Episodes = () => {
  const [watching, setWatching] = useState(null);
  const { EPISODES, LATEST_EPISODE, SITE } = useContent();
  const meta = useSectionMeta("episodes");
  const visible = EPISODES.filter((ep) => !ep.hidden);

  return (
    <section id="episodes" className="relative py-24 md:py-36" data-testid="episodes-section">
      <div className="absolute right-0 top-0 w-[420px] h-[420px] rounded-full bg-burgundy/30 blur-[160px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Chapter number={meta.number} label={meta.label} id="episodes" />
        </Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
          <Reveal delay={0.08}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="episodes-heading">
              THE <span className="italic text-gold-light">CONVERSATIONS</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-sm text-smoke max-w-xs leading-relaxed">
              {SITE.episodesSide}
            </p>
          </Reveal>
        </div>

        <div
          className={
            visible.length > 1
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
              : "grid grid-cols-1 max-w-2xl mx-auto"
          }
        >
          {visible.map((ep, i) => (
            <EpisodeCard key={ep.num} ep={ep} index={i} isLatest={ep.num === LATEST_EPISODE.num} onWatch={setWatching} />
          ))}
        </div>
      </div>

      <WatchModal episode={watching} open={!!watching} onOpenChange={(o) => !o && setWatching(null)} onSelectEpisode={setWatching} />
    </section>
  );
};
